import * as actions from "actions/collections";
import { parseDate } from "components/product-collection/market-settings/market-settings-modal";

import {
    AssortmentMarketStatus,
    MarketSettingsData,
} from "components/product-collection/market-settings/market-settings.types";
import { COLLECTIONS, MUTATE_OPERATION_ENHANCED } from "constants/action-types";
import {
    ALLOCATION_BAG_ID,
    ITEMS_ID,
    SizeCurveStatus,
    SizingErrors,
    CollectionKeys,
    Kinds,
    MARKET_SETTINGS_DEPARTMENT_LOOKUP_KEY,
} from "constants/product-collection";
import { ItemKind } from "containers/folders/types/base";
import { nameExtRemover } from "containers/folders/utils/space-item";
import { amphibiousGetIn } from "helpers/objUtils";
import { IdKeyTypes } from "helpers/product-collection";
import { Observable } from "rxjs";
import { findOneFieldByKind } from "./helpers/allocation";
import {
    fetchSuggestionsFromInternalLookup,
    getExecuteSqlResultCache,
} from "helpers/internal-lookups/size-curves-lookup";

const DEBOUNCE_MS = 500;

export const collectionSendReportOnCalculateTotalsReturned = (action$, store) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.REPORTING.UPDATES.START)
        .debounce(() => Observable.timer(DEBOUNCE_MS))
        .flatMap(({ payload }) => {
            const state = store.getState();
            const {
                collection: { driveItem, driveItemExportCount },
                mutate,
            } = state;

            const defaultValue = 0;
            const request: MarketSettingsData = {
                assortmentName: nameExtRemover(driveItem?.name, driveItem?.kind as ItemKind),
                departmentId: driveItem?.collection_facet?.department_id,
                marketDate: parseDate(driveItem?.collection_facet?.market_date),
                dueDate: parseDate(driveItem?.collection_facet?.due_date),
                brandContactEmail: driveItem?.collection_facet?.brand_contact_email,
                brandContactName: driveItem?.collection_facet?.brand_contact_name,
                marketStatus: driveItem?.collection_facet?.market_status as AssortmentMarketStatus,
                rowCount: Number(driveItem?.collection_facet?.row_count) || defaultValue,
                smuCount: Number(driveItem?.collection_facet?.smu_count) || defaultValue,
                totalCost: driveItem?.collection_facet?.total_cost || defaultValue,
                totalRetailValue: driveItem?.collection_facet?.total_retail_value || defaultValue,
                quantityRowsWithUnits: Number(driveItem?.collection_facet?.row_with_units_count) || defaultValue,
                totalSized: Number(driveItem?.collection_facet?.total_sized) || defaultValue,
                totalUnits: Number(driveItem?.collection_facet?.total_units),
                exportCount: Number(driveItem?.collection_facet?.export_count),
                departmentAndNumbers: driveItem?.collection_facet?.department_from_rows,
            };

            const snapshot = state.mutate.snapshots.get(ITEMS_ID);

            const totalItemsCount = snapshot?.size;

            const shouldUpdateRowCount = totalItemsCount !== request.rowCount;
            if (shouldUpdateRowCount) {
                request.rowCount = totalItemsCount;
            }

            const totalSized = snapshot.reduce((accumulator, currentItem) => {
                if (!amphibiousGetIn(currentItem, ["fields", IdKeyTypes.ProductId, "string_value"])) {
                    return accumulator + 1;
                }

                return accumulator;
            }, 0);

            const shouldUpdateSMUCount = totalSized !== request.smuCount;
            if (shouldUpdateSMUCount) {
                request.smuCount = totalSized;
            }

            const totals = {
                retail: payload.totals?._total?.retail,
                wholesale: payload.totals?._total?.wholesale,
                units: payload.totals?._total.units,
            };

            const shouldUpdateTotalRetail =
                totals.retail !== undefined && totals.retail.toString() !== request.totalRetailValue;
            if (shouldUpdateTotalRetail) {
                request.totalRetailValue = totals.retail.toString();
            }

            const shouldUpdateTotalCost =
                totals.wholesale !== undefined && totals.wholesale.toString() !== request.totalCost;
            if (shouldUpdateTotalCost) {
                request.totalCost = totals.wholesale.toString();
            }

            const shouldUpdateTotalUnits = totals.units !== undefined && totals.units !== request.totalUnits;
            if (shouldUpdateTotalUnits) {
                request.totalUnits = totals.units;
            }

            const isNewAllocationBag = state.mutate.snapshots.hasIn([ALLOCATION_BAG_ID]);

            if (isNewAllocationBag) {
                const bagValues = state.mutate.snapshots.get(ALLOCATION_BAG_ID);
                const countOfProductsWithUnitsEntered = bagValues.reduce((accumulator, currentItem) => {
                    const fields = amphibiousGetIn(currentItem, ["map", "fields"]);
                    const hasUnitsInsideTheFields = fields?.some(
                        (item) => amphibiousGetIn(item, ["string_value"]) !== "0"
                    );

                    return hasUnitsInsideTheFields ? accumulator + 1 : accumulator;
                }, 0);

                request.quantityRowsWithUnits = countOfProductsWithUnitsEntered ? countOfProductsWithUnitsEntered : 0;
            } else {
                const productsWithTotalUnitsEntered = snapshot.reduce((accumulator, currentItem) => {
                    const bulks = currentItem.get("bulks");
                    const unitsByDoors = bulks?.get("r");

                    return unitsByDoors?.size ? accumulator + 1 : accumulator;
                }, 0);

                request.quantityRowsWithUnits = productsWithTotalUnitsEntered;

                if (snapshot.some((item) => !!amphibiousGetIn(item, ["fields", "_size_status", "string_value"]))) {
                    const notHasError = (value) =>
                        ![
                            SizeCurveStatus.None,
                            SizeCurveStatus.Error,
                            SizingErrors.SizeTotalsDotNotMatchBulks,
                        ].includes(value);
                    const productsSized = snapshot.filter(
                        (item) =>
                            amphibiousGetIn(item, ["fields", "_size_status", "string_value"]) &&
                            notHasError(amphibiousGetIn(item, ["fields", "_size_status", "string_value"]))
                    );

                    request.totalSized = productsSized ? productsSized?.size : 0;
                }
            }

            const exportCount = driveItemExportCount;

            if (exportCount) {
                request.exportCount = exportCount;
            }

            // This is used only when the merchant not have doors
            // is calculated by total.worker.js
            const totalHasRowsWithUnits = payload.totals?._total?.hasOwnProperty("rows_with_units");
            if (totalHasRowsWithUnits) {
                request.quantityRowsWithUnits = payload.totals?._total?.rows_with_units;
            }

            const currentCollection = mutate?.snapshots && mutate?.snapshots?.get(state?.collection?.collectionId);
            const arrBrandName: string[] = [];
            const itemDisplayProperty = currentCollection?.getIn(CollectionKeys.ITEM_DISPLAY_PROPERTY.KEYS);
            const _rawitemDisplayProperty = itemDisplayProperty?.toJS();
            const _departmentLookup = findOneFieldByKind(Kinds.DEPARTMENT_LOOKUP, _rawitemDisplayProperty || []);
            const departNameAndNumber = _departmentLookup?.lookup_plan?.select?.terms;
            const arrDepartmentAndNumbers: string[] = [];
            const displayRowsCount = state?.collection?.gridApi?.getDisplayedRowCount() || 0;
            if (displayRowsCount > 0) {
                for (let i = 0; i < displayRowsCount; i++) {
                    const currentRow = state?.collection?.gridApi?.getDisplayedRowAtIndex(i);
                    if (departNameAndNumber && departNameAndNumber.length > 0) {
                        const deptNumber = amphibiousGetIn(currentRow?.data, [
                            "fields",
                            departNameAndNumber[0]?.column?.schema_column,
                            "string_value",
                        ]);
                        const deptName = amphibiousGetIn(currentRow?.data, [
                            "fields",
                            departNameAndNumber[1]?.column?.schema_column,
                            "string_value",
                        ]);
                        if (deptNumber && deptName) {
                            arrDepartmentAndNumbers.push(`${deptNumber} ${deptName}`);
                        } else {
                            if (deptNumber) {
                                arrDepartmentAndNumbers.push(deptNumber);
                            }
                            if (deptName) {
                                arrDepartmentAndNumbers.push(deptName);
                            }
                        }
                    }
                }
                if (arrDepartmentAndNumbers?.length > 0) {
                    request.departmentAndNumbers = [...new Set(arrDepartmentAndNumbers)]?.join(", ");
                }
            }

            const merchantBrandNameProperty = findOneFieldByKind(Kinds.BRAND_NAME, _rawitemDisplayProperty || []);
            if (merchantBrandNameProperty) {
                for (let i = 0; i < displayRowsCount; i++) {
                    const currentRow = state?.collection?.gridApi?.getDisplayedRowAtIndex(i);
                    const brandname = amphibiousGetIn(currentRow?.data, [
                        "fields",
                        merchantBrandNameProperty?.field_name,
                        "string_value",
                    ]);
                    if (brandname && !arrBrandName.includes(brandname)) {
                        arrBrandName.push(brandname);
                    }
                }
                request.brandName = arrBrandName?.join(", ");
            }

            const getDepartmentNameFromMarketSettingLookup = (suggestions, departmentId, lookUpField) => {
                const departmentObject = suggestions?.find((option) => option?.key === departmentId);
                const lookupFieldName = lookUpField?.lookup_plan?.select?.terms[1]?.column?.table_column;
                const departmentName =
                    amphibiousGetIn(departmentObject, ["row", lookupFieldName, "string_value"]) || "";
                return departmentName;
            };

            const lookUpField = _rawitemDisplayProperty?.find((item) => item?.kind === Kinds.DEPARTMENT_LOOKUP);
            const merchantId = currentCollection?.getIn(["merchant_id", "string_value"], "");
            // This should be the last condition in current code block as this is returning the action.
            if (
                driveItem?.collection_facet?.department_id &&
                currentCollection &&
                lookUpField &&
                _rawitemDisplayProperty &&
                merchantId
            ) {
                const marketSettingsDepartmentLookupCache = getExecuteSqlResultCache();
                const requestKey = MARKET_SETTINGS_DEPARTMENT_LOOKUP_KEY;
                // Using cached lookup value if present
                if (marketSettingsDepartmentLookupCache.has(requestKey)) {
                    const lookup = marketSettingsDepartmentLookupCache.get(requestKey);
                    request.departmentName = getDepartmentNameFromMarketSettingLookup(
                        lookup.suggestions,
                        driveItem?.collection_facet?.department_id,
                        lookUpField
                    );
                    const action = [store.dispatch(actions.updateCollectionSettings(request))];
                    return action;
                } else {
                    return Observable.from(
                        fetchSuggestionsFromInternalLookup({
                            fieldSchema: lookUpField,
                            merchantId,
                            rawItemDisplayProperty: _rawitemDisplayProperty,
                        })
                    ).mergeMap((res) => {
                        if (res && res?.suggestions && res?.suggestions?.length > 0) {
                            // Caching the response if already not cached
                            marketSettingsDepartmentLookupCache.set(requestKey, res);
                            const deptLookup = marketSettingsDepartmentLookupCache?.get(requestKey);
                            request.departmentName = getDepartmentNameFromMarketSettingLookup(
                                deptLookup.suggestions,
                                driveItem?.collection_facet?.department_id,
                                lookUpField
                            );
                        }
                        const action = [store.dispatch(actions.updateCollectionSettings(request))];
                        return action;
                    });
                }
            }
            const action = [store.dispatch(actions.updateCollectionSettings(request))];
            return action;
        });
};

// We create this Epic to call collectionSendReportOnCalculateTotalsReturned() because when we change size unity
// We need send reportings too. We only want to do this, if there is a change to `_size_status`
export const collectionSendReportOnMutate = (action$, store) => {
    return action$
        .ofType(MUTATE_OPERATION_ENHANCED)
        .debounce(() => Observable.timer(DEBOUNCE_MS))
        .filter(({ payload: { objectId, operations } }) => {
            const isItemOrBag = objectId === ITEMS_ID || objectId === ALLOCATION_BAG_ID;

            return (
                isItemOrBag &&
                operations?.some((operation) => {
                    return operation?.deltas?.some((delta) => {
                        const pathOperations = delta?.get("path");
                        return pathOperations?.includes("_size_status");
                    });
                })
            );
        })
        .flatMap((payload) => {
            const state = store.getState();
            const {
                collection: { doorTotals, driveItem },
            } = state;
            const { capabilities } = driveItem;

            const action = [];

            if (capabilities?.can_edit) {
                action.push(store.dispatch(actions.updateCollectionReports(doorTotals)));
            }

            return action;
        });
};
